import BooleanFilterItem from './filter-types/BooleanFilterItem';
import CurrencyFilterItem from './filter-types/CurrencyFilterItem';
import DateFilterItem from './filter-types/DateFilterItem';
import DateRangeFilterItem from './filter-types/DateRangeFilterItem';
import ListFilterItem from './filter-types/ListFilterItem';
import ModuleFilterItem from './filter-types/ModuleFilterItem';
import NumberFilterItem from './filter-types/NumberFilterItem';
import TextFilterItem from './filter-types/TextFilterItem';

import FilterItem from './FilterItem';

export interface DateRangeType {
	from?: Date;
	to?: Date;
}

export type MiddlewhereFilterValue = string | number | string[] | number[] | Date | DateRangeType | null;

export interface MiddlewhereFilter {
	filt_field: string;
	filt_clause: string;
	filt_value?: MiddlewhereFilterValue;
}

export type FilterType = {
	filt_field: string;
	filt_label: string;
	filt_type: string;
	filt_table?: string;
	filt_size: number;
	filt_default_clause?: string;
	filt_current_clause: string;
	filt_default_value?: MiddlewhereFilterValue;
	filt_current_value: MiddlewhereFilterValue;
	filt_unit?: string;
};

export interface FilterItemBaseProps<T> {
	className?: string;
	placeholder?: string;
	name: string;
	operator: string;
	value?: T;
	defaultValue?: T;
	sendChange: (newValue?: T, newOperator?: string) => void;
}

type FilterItemManagerProps<T> = {
	filter: FilterType;
	handleFilterChange: (name: string, operator: string, value: T) => void;
};

export default function FilterItemManager(props: FilterItemManagerProps<MiddlewhereFilterValue>) {
	const {
		filt_field: filterField,
		filt_type: filterType,
		filt_table: filterTable,
		filt_unit: filterUnit
	} = props.filter;

	const FilterItemWrapper = ({
		renderValueComponent
	}: {
		renderValueComponent: (props: FilterItemBaseProps<any>) => React.ReactNode;
	}) => (
		<FilterItem
			filter={props.filter}
			renderValueComponent={renderValueComponent}
			handleFilterChange={props.handleFilterChange}
		/>
	);

	if (filterField === 'comp_ateco_id')
		return (
			<FilterItemWrapper
				renderValueComponent={({ name, value, defaultValue, operator, sendChange }) => (
					<ListFilterItem
						name={name}
						operator={operator}
						systable="companies_ateco"
						value={typeof value === 'string' ? JSON.parse(value) : value}
						defaultValue={defaultValue}
						getOptionLabel={option => `${option.coat_code} - ${option.coat_title}`}
						filterOptions={{
							stringify: option => `${option.coat_code} ${option.caty_title} ${option.coat_title}`
						}}
						groupBy={option => `${option.coat_type} - ${option.caty_title}`}
						sendChange={sendChange}
					/>
				)}
			/>
		);

	if (filterType === 'text')
		return (
			<FilterItemWrapper
				renderValueComponent={({ name, value, defaultValue, operator, sendChange }) => (
					<TextFilterItem
						name={name}
						operator={operator}
						value={value ? `${value}` : ''}
						defaultValue={defaultValue ? `${defaultValue}` : ''}
						sendChange={sendChange}
					/>
				)}
			/>
		);

	if (filterType === 'number')
		return (
			<FilterItemWrapper
				renderValueComponent={({ name, value, defaultValue, operator, sendChange }) => (
					<NumberFilterItem
						name={name}
						operator={operator}
						value={value}
						defaultValue={defaultValue}
						sendChange={sendChange}
						unit={props.filter.filt_unit}
					/>
				)}
			/>
		);

	if (filterType === 'decimal')
		return (
			<FilterItemWrapper
				renderValueComponent={({ name, value, defaultValue, operator, sendChange }) => (
					<NumberFilterItem
						name={name}
						operator={operator}
						value={value}
						defaultValue={defaultValue}
						sendChange={sendChange}
						unit={filterUnit}
					/>
				)}
			/>
		);

	if (filterType === 'currency')
		return (
			<FilterItemWrapper
				renderValueComponent={({ name, value, defaultValue, operator, sendChange }) => (
					<CurrencyFilterItem
						name={name}
						operator={operator}
						value={value}
						defaultValue={defaultValue}
						sendChange={sendChange}
					/>
				)}
			/>
		);

	if (filterType === 'date')
		return (
			<FilterItemWrapper
				renderValueComponent={({ name, value, defaultValue, operator, sendChange }) =>
					operator === 'range' ? (
						<DateRangeFilterItem
							name={name}
							operator={operator}
							value={value}
							defaultValue={defaultValue}
							sendChange={sendChange}
						/>
					) : (
						<DateFilterItem
							name={name}
							operator={operator}
							value={value}
							defaultValue={defaultValue}
							sendChange={sendChange}
						/>
					)
				}
			/>
		);

	if (filterType === 'boolean')
		return (
			<FilterItemWrapper
				renderValueComponent={({ name, value, defaultValue, operator, sendChange }) => (
					<BooleanFilterItem
						name={name}
						operator={operator}
						defaultValue={defaultValue}
						sendChange={sendChange}
					/>
				)}
			/>
		);

	if ((filterType === 'list' || filterType === 'child-list') && filterTable !== undefined)
		return (
			<FilterItemWrapper
				renderValueComponent={({ name, value, defaultValue, operator, sendChange }) => (
					<ListFilterItem
						name={name}
						operator={operator}
						systable={filterTable}
						value={typeof value === 'string' ? JSON.parse(value) : value}
						defaultValue={defaultValue}
						sendChange={sendChange}
					/>
				)}
			/>
		);

	if (filterType === 'module' && filterTable !== undefined)
		return (
			<FilterItemWrapper
				renderValueComponent={({ name, value, defaultValue, operator, sendChange }) => (
					<ModuleFilterItem
						name={name}
						operator={operator}
						module={filterTable}
						uid={value}
						defaultValue={defaultValue}
						sendChange={sendChange}
					/>
				)}
			/>
		);

	if (filterType === 'custom')
		return (
			<FilterItemWrapper
				renderValueComponent={({ name, value, defaultValue, operator, sendChange }) => (
					<BooleanFilterItem
						name={name}
						operator={operator}
						defaultValue={defaultValue}
						sendChange={sendChange}
					/>
				)}
			/>
		);

	// TODO: aggiungere tipo "datetime" e "slider"
	return <></>;
}
